import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, BookOpen, Sparkles, Trees, Wrench, Home, ArrowRight, Calendar, X, ChevronRight, ChevronLeft, Play, Pause, RotateCcw, Share2, Video } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { SEOHead } from '../components/SEOHead';
import { ShareButtons } from './ConseilPage';
import axios from 'axios';
import { logError } from '../lib/logger';
import { ConseilMedia } from '../components/ConseilMedia';
import { VIEWPORT_ONCE } from '../lib/motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const CATS = [
  { id: 'all', label: 'Tous', icon: Sparkles },
  { id: 'menage', label: 'Ménage & Nettoyage', icon: Home },
  { id: 'jardinage', label: 'Jardinage', icon: Trees },
  { id: 'bricolage', label: 'Bricolage', icon: Wrench },
];

const TYPE_ICONS = { astuce: Lightbulb, tuto: BookOpen };
const TYPE_LABELS = { astuce: 'Astuce du jour', tuto: 'Tutoriel de la semaine' };
const CAT_LABELS = { menage: 'Ménage', jardinage: 'Jardinage', bricolage: 'Bricolage' };
const catLabel = (category) => CAT_LABELS[category] || CAT_LABELS.bricolage;
const TYPE_FILTERS = [
  { id: 'all', label: <>Tous</> },
  { id: 'astuce', label: <><Lightbulb className="h-3.5 w-3.5 mr-1" /> Astuces</> },
  { id: 'tuto', label: <><BookOpen className="h-3.5 w-3.5 mr-1" /> Tutoriels</> },
];

/** Story-style progress: past slides are full, the current one fills up, the next ones are empty. */
const slideProgressWidth = (index, step, progress) => {
  if (index < step + 1) return '100%';
  if (index === step + 1) return `${progress}%`;
  return '0%';
};

/** Background illustration opacity: full on the intro slide, softened behind the text slides. */
const backgroundOpacity = (step, hasVideo) => {
  if (step === -1) return 1;
  return hasVideo ? 0.3 : 0.18;
};
const CAT_COLORS = {
  menage: { bg: 'bg-blue-100 text-blue-700', border: 'border-blue-200', glow: 'shadow-blue-100' },
  jardinage: { bg: 'bg-green-100 text-green-700', border: 'border-green-200', glow: 'shadow-green-100' },
  bricolage: { bg: 'bg-orange-100 text-orange-700', border: 'border-orange-200', glow: 'shadow-orange-100' },
};

// Floating animated icons for hero
function FloatingIcons() {
  const icons = [
    { Icon: Sparkles, x: '10%', y: '20%', delay: 0, size: 20 },
    { Icon: Trees, x: '85%', y: '30%', delay: 0.5, size: 24 },
    { Icon: Wrench, x: '75%', y: '70%', delay: 1, size: 18 },
    { Icon: Home, x: '15%', y: '75%', delay: 1.5, size: 22 },
    { Icon: Lightbulb, x: '50%', y: '10%', delay: 0.3, size: 20 },
  ];
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {icons.map((item, i) => (
        <motion.div
          key={`${item.x}-${item.y}`}
          className="absolute text-[hsl(var(--primary))]/10"
          style={{ left: item.x, top: item.y }}
          animate={{ y: [0, -15, 0], rotate: [0, 10, -10, 0], opacity: [0.15, 0.25, 0.15] }}
          transition={{ duration: 4 + i, repeat: Infinity, delay: item.delay, ease: "easeInOut" }}
        >
          <item.Icon style={{ width: item.size, height: item.size }} />
        </motion.div>
      ))}
    </div>
  );
}

const CAT_ICONS = { menage: Home, jardinage: Trees, bricolage: Wrench };

// Animated tutorial player - like a video slideshow
function TutoPlayer({ item, onClose }) {
  const [step, setStep] = useState(-1); // -1 = intro
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  
  const cat = CAT_COLORS[item.category] || CAT_COLORS.menage;
  const CatIcon = CAT_ICONS[item.category] || Home;
  
  // Parse steps from content
  const rawSteps = (item.contenu || '').split('\n\n').filter(s => s.trim());
  const steps = rawSteps.map((text, i) => {
    const cleaned = text.replace(/^\d+[\.\)]\s*/, '').replace(/^\*\*.*?\*\*\s*:?\s*/, '');
    const titleMatch = text.match(/^(?:\d+[\.\)]\s*)?(?:\*\*(.*?)\*\*\s*:?\s*)?(.*)$/s);
    return {
      num: i + 1,
      title: titleMatch?.[1] || `Étape ${i + 1}`,
      text: titleMatch?.[2] || cleaned || text,
    };
  });
  
  const totalSlides = steps.length + 1; // +1 for intro
  const SLIDE_DURATION = 5000;

  // Auto-play
  useEffect(() => {
    if (!playing) return;
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          setStep(s => {
            if (s >= steps.length - 1) { setPlaying(false); return s; }
            return s + 1;
          });
          return 0;
        }
        return p + (100 / (SLIDE_DURATION / 50));
      });
    }, 50);
    return () => clearInterval(interval);
  }, [playing, steps.length]);

  const goTo = (s) => { setStep(s); setProgress(0); };
  const restart = () => { setStep(-1); setProgress(0); setPlaying(true); };

  return (
    <motion.div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <motion.div
        className="relative bg-[hsl(var(--foreground))] rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden"
        initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 30 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={e => e.stopPropagation()}
        style={{ aspectRatio: '16/9' }}
      >
        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 z-20 flex gap-1 p-2">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <div key={`slide-${i}`} className="flex-1 h-1 rounded-full bg-white/20 overflow-hidden cursor-pointer" onClick={() => goTo(i - 1)}>
              <motion.div
                className="h-full bg-white rounded-full"
                style={{ width: slideProgressWidth(i, step, progress) }}
              />
            </div>
          ))}
        </div>

        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 z-20 p-2 rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors">
          <X className="h-5 w-5" />
        </button>

        {/* Background illustration (intro + soft on steps) */}
        {(item.image_url || item.video_url) && (
          <motion.div
            className="absolute inset-0 z-0"
            animate={{ opacity: backgroundOpacity(step, Boolean(item.video_url)) }}
            transition={{ duration: 0.8 }}
            data-testid="tuto-player-image"
          >
            {item.video_url ? (
              <ConseilMedia item={item} className="w-full h-full" testId="tuto-player-media" />
            ) : (
              <motion.img
                src={item.image_url}
                alt=""
                className="w-full h-full object-cover"
                initial={{ scale: 1 }}
                animate={{ scale: [1, 1.08] }}
                transition={{ duration: 18, repeat: Infinity, repeatType: 'reverse', ease: 'linear' }}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/30" />
          </motion.div>
        )}

        {/* Content area */}
        <div className="relative z-10 h-full flex flex-col justify-center items-center p-8 sm:p-12 text-white">
          <AnimatePresence mode="wait">
            {step === -1 ? (
              /* Intro slide */
              <motion.div
                key="intro"
                className="text-center"
                initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
              >
                <motion.div
                  className="w-20 h-20 rounded-2xl bg-[hsl(var(--primary))] flex items-center justify-center mx-auto mb-6"
                  animate={{ rotate: [0, 5, -5, 0], scale: [1, 1.05, 1] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <CatIcon className="h-10 w-10 text-white" />
                </motion.div>
                <Badge className="mb-4 bg-white/20 text-white hover:bg-white/20 border-0">
                  <BookOpen className="h-3.5 w-3.5 mr-1" /> Tutoriel
                </Badge>
                <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-bold mb-3 drop-shadow">{item.titre}</h2>
                <p className="text-white/80 text-sm max-w-md mx-auto">{item.resume}</p>
                {(item.duree || item.difficulte || item.materiel?.length) ? (
                  <div className="flex flex-wrap justify-center gap-2 mt-4" data-testid="tuto-player-meta">
                    {item.duree && <span className="px-3 py-1 rounded-full bg-white/15 backdrop-blur text-xs">⏱ {item.duree}</span>}
                    {item.difficulte && <span className="px-3 py-1 rounded-full bg-white/15 backdrop-blur text-xs">Niveau : {item.difficulte}</span>}
                    {item.materiel?.length > 0 && <span className="px-3 py-1 rounded-full bg-white/15 backdrop-blur text-xs">{item.materiel.length} éléments de matériel</span>}
                  </div>
                ) : null}
                <motion.p
                  className="text-white/50 text-xs mt-6"
                  animate={{ opacity: [0.4, 0.8, 0.4] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  {steps.length} étapes • Lecture automatique
                </motion.p>
              </motion.div>
            ) : step < steps.length ? (
              /* Step slides */
              <motion.div
                key={`step-${step}`}
                className="w-full max-w-lg"
                initial={{ opacity: 0, x: 80 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -80 }}
                transition={{ duration: 0.4 }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <motion.div
                    className="w-14 h-14 rounded-xl bg-[hsl(var(--primary))] flex items-center justify-center flex-shrink-0"
                    initial={{ scale: 0, rotate: -90 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", delay: 0.2 }}
                  >
                    <span className="text-xl font-bold text-white">{steps[step].num}</span>
                  </motion.div>
                  <motion.h3
                    className="font-['Space_Grotesk'] text-lg sm:text-xl font-semibold"
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                  >
                    {steps[step].title}
                  </motion.h3>
                </div>
                <motion.p
                  className="text-white/80 leading-relaxed text-sm sm:text-base"
                  initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                >
                  {steps[step].text}
                </motion.p>
                <motion.div
                  className="mt-6 text-white/30 text-xs"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
                >
                  Étape {step + 1} / {steps.length}
                </motion.div>
              </motion.div>
            ) : null}
          </AnimatePresence>

          {/* End screen */}
          {!playing && step >= steps.length - 1 && (
            <motion.div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                <Sparkles className="h-12 w-12 text-[hsl(var(--primary))] mb-4" />
              </motion.div>
              <h3 className="font-['Space_Grotesk'] text-xl font-bold text-white mb-2">Tutoriel terminé !</h3>
              <p className="text-white/60 text-sm mb-6">Besoin d'aide ? Nos Easers sont là pour vous.</p>
              <div className="flex gap-3">
                <Button variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10" onClick={restart}>
                  <RotateCcw className="h-4 w-4 mr-1" /> Revoir
                </Button>
                <Link to="/devis" onClick={onClose}>
                  <Button size="sm" className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">
                    Demander un devis <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}
        </div>

        {/* Bottom controls */}
        <div className="absolute bottom-0 left-0 right-0 z-20 p-4 flex items-center justify-between bg-gradient-to-t from-black/50 to-transparent">
          <div className="flex items-center gap-2">
            <button onClick={() => goTo(Math.max(-1, step - 1))} className="p-1.5 rounded-full hover:bg-white/10 text-white/60 hover:text-white transition-colors">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button onClick={() => setPlaying(!playing)} className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
              {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </button>
            <button onClick={() => { if (step < steps.length - 1) goTo(step + 1); }} className="p-1.5 rounded-full hover:bg-white/10 text-white/60 hover:text-white transition-colors">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          <span className="text-white/40 text-xs">{catLabel(item.category)} • Ptitservices.com</span>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Expanded card modal for tips
function ConseilModal({ item, onClose }) {
  if (!item) return null;
  const TypeIcon = TYPE_ICONS[item.type] || Lightbulb;
  const cat = CAT_COLORS[item.category] || CAT_COLORS.menage;

  return (
    <motion.div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <motion.div
        className="relative bg-card rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
        initial={{ scale: 0.9, y: 30 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 30 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={e => e.stopPropagation()}
      >
        {/* Illustration */}
        {(item.image_url || item.video_url) && (
          <div className="relative h-48 sm:h-60 overflow-hidden" data-testid="conseil-modal-image">
            <ConseilMedia item={item} className="w-full h-full" testId="conseil-modal-media" showBadge />
            <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent pointer-events-none" />
          </div>
        )}
        {/* Header */}
        <div className={`p-6 border-b ${cat.border} bg-gradient-to-r from-[hsl(var(--brand-mint))]/30 to-transparent`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <Badge className={`${cat.bg} hover:opacity-90`}>
                  {catLabel(item.category)}
                </Badge>
                <Badge variant="secondary">
                  <TypeIcon className="h-3 w-3 mr-1" />
                  {TYPE_LABELS[item.type] || item.type}
                </Badge>
              </div>
              <h2 className="font-['Space_Grotesk'] text-xl sm:text-2xl font-semibold">{item.titre}</h2>
            </div>
            <button onClick={onClose} className="ml-4 p-2 rounded-full hover:bg-[hsl(var(--secondary))] transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="prose prose-sm max-w-none">
            {(item.contenu || '').split('\n\n').map((paragraph, i) => (
              <motion.p
                key={`${i}-${paragraph.slice(0, 24)}`}
                className="text-muted-foreground leading-relaxed mb-4 whitespace-pre-line"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.08 }}
              >
                {paragraph}
              </motion.p>
            ))}
          </div>

          {(item.pourquoi || item.erreur) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2" data-testid="conseil-modal-extras">
              {item.pourquoi && (
                <div className="p-4 rounded-xl bg-[hsl(var(--brand-mint))]/30 border border-[hsl(var(--primary))]/15">
                  <p className="text-xs font-semibold text-[hsl(var(--primary))] mb-1 flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> Pourquoi ça marche</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.pourquoi}</p>
                </div>
              )}
              {item.erreur && (
                <div className="p-4 rounded-xl bg-[hsl(var(--accent))]/10 border border-[hsl(var(--accent))]/20">
                  <p className="text-xs font-semibold text-[hsl(var(--accent))] mb-1 flex items-center gap-1"><X className="h-3.5 w-3.5" /> L'erreur à éviter</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.erreur}</p>
                </div>
              )}
            </div>
          )}

          {item.tags && item.tags.length > 0 && (
            <motion.div
              className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-border"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {item.tags.map(tag => (
                <span key={tag} className="text-xs px-3 py-1 rounded-full bg-[hsl(var(--secondary))] text-muted-foreground">#{tag}</span>
              ))}
            </motion.div>
          )}

          <motion.div
            className="mt-6 pt-4 border-t border-border flex items-center justify-between"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {item.date ? new Date(item.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
            </span>
            <div className="flex items-center gap-2">
              {item.slug && <ShareButtons url={`/ptitconseils/${item.slug}`} title={item.titre} compact />}
              <Link to="/devis">
                <Button size="sm" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white">
                  Besoin d'aide ? <ArrowRight className="ml-1 h-3.5 w-3.5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function PtitconseilsPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/ptitconseils?limit=50`;
      if (filter !== 'all') url += `&category=${filter}`;
      if (typeFilter !== 'all') url += `&type=${typeFilter}`;
      const res = await axios.get(url);
      setItems(res.data.items);
      setCounts(res.data.counts);
      setTotal(res.data.total);
    } catch (err) {
      logError('chargement des Ptitconseils', err);
    } finally {
      setLoading(false);
    }
  }, [filter, typeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1,2,3].map(i => (
            <motion.div key={`skeleton-${i}`} className="h-48 rounded-xl bg-[hsl(var(--secondary))]/60"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          ))}
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <motion.div className="text-center py-12" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 2, repeat: Infinity }}>
            <Lightbulb className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
          </motion.div>
          <p className="text-muted-foreground mb-2">Les premiers conseils arrivent bientôt !</p>
        </motion.div>
      );
    }
    return (
      <AnimatePresence mode="wait">
        <motion.div
          key={`${filter}-${typeFilter}`}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          {items.map((item, index) => {
            const TypeIcon = TYPE_ICONS[item.type] || Lightbulb;
            const cat = CAT_COLORS[item.category] || CAT_COLORS.menage;
            return (
              <motion.div
                key={item._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.06 }}
                whileHover={{ y: -6, transition: { duration: 0.2 } }}
              >
                <Card
                  className={`h-full flex flex-col cursor-pointer border-2 border-transparent hover:${cat.border} hover:shadow-lg ${cat.glow} transition-all duration-300 group overflow-hidden`}
                  onClick={() => setSelectedItem(item)}
                  data-testid="conseil-card"
                >
                  {item.image_url ? (
                    <div className="relative h-40 overflow-hidden" data-testid="conseil-card-image">
                      <img src={item.image_url} alt={item.titre} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      {item.video_url && (
                        <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-black/60 text-white backdrop-blur-sm" data-testid="conseil-card-video-badge">
                          <Video className="h-3 w-3" /> Vidéo
                        </span>
                      )}
                      {item.type === 'tuto' && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform"><Play className="h-5 w-5 text-[hsl(var(--primary))] ml-0.5" /></div>
                        </div>
                      )}
                      {item.duree && <span className="absolute bottom-2 right-2 text-[11px] px-2 py-0.5 rounded-full bg-black/60 text-white">⏱ {item.duree}</span>}
                    </div>
                  ) : (
                    <div className="h-2 bg-[hsl(var(--brand-mint))]" />
                  )}
                  <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className={`${cat.bg} hover:opacity-90 text-xs`}>
                      {catLabel(item.category)}
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      <TypeIcon className="h-3 w-3 mr-1" />
                      {item.type === 'astuce' ? 'Astuce' : 'Tuto'}
                    </Badge>
                  </div>
                  <h3 className="font-['Space_Grotesk'] font-semibold text-base mb-2 group-hover:text-[hsl(var(--primary))] transition-colors">
                    {item.type === 'tuto' && <Play className="h-3.5 w-3.5 inline mr-1.5 text-[hsl(var(--primary))]" />}
                    {item.titre}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3 flex-1">
                    {item.resume || (item.contenu ? item.contenu.substring(0, 120) + '...' : '')}
                  </p>
                  {item.tags && item.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {item.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-[hsl(var(--secondary))] text-muted-foreground">#{tag}</span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between text-xs text-muted-foreground mt-auto pt-3 border-t border-border">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {item.date ? new Date(item.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : ''}
                    </span>
                    <span className="flex items-center gap-2">
                      {item.slug && (
                        <Link
                          to={`/ptitconseils/${item.slug}`}
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1 hover:text-[hsl(var(--primary))] transition-colors"
                          data-testid="conseil-page-link"
                          aria-label={`Ouvrir la page de ${item.titre}`}
                        >
                          <Share2 className="h-3 w-3" /> Page
                        </Link>
                      )}
                      <span className="flex items-center gap-1 text-[hsl(var(--primary))] opacity-0 group-hover:opacity-100 transition-opacity">
                        Lire <ChevronRight className="h-3 w-3" />
                      </span>
                    </span>
                  </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      </AnimatePresence>
    );
  };

  return (
    <div>
      <SEOHead
        title="Ptitconseils : astuces ménage et jardin"
        description="Découvrez nos astuces et tutoriels gratuits : nettoyage, jardinage, bricolage. Un conseil par jour, un tuto par semaine par les experts Ptitservices.com."
        path="/ptitconseils"
      />

      {/* Hero */}
      <section className="relative bg-[linear-gradient(135deg,hsla(195,20%,97%,1)_0%,hsla(182,40%,94%,1)_55%,hsla(195,20%,97%,1)_100%)] py-12 sm:py-16 overflow-hidden">
        <FloatingIcons />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Ptitconseils</Badge>
            <h1 className="font-['Space_Grotesk'] text-3xl sm:text-4xl font-semibold tracking-tight mb-4">
              Astuces & Tutoriels <span className="text-[hsl(var(--primary))]">par nos experts</span>
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto text-base sm:text-lg mb-6">
              Chaque jour une astuce, chaque semaine un tutoriel. Ménage, jardinage, bricolage : profitez des conseils de nos Easers.
            </p>
          </motion.div>
          <motion.div
            className="flex flex-wrap justify-center gap-3"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {[
              { icon: Lightbulb, text: '1 astuce / jour', color: 'text-amber-500' },
              { icon: BookOpen, text: '1 tuto / semaine', color: 'text-[hsl(var(--primary))]' },
              { icon: Sparkles, text: `${total} conseils publiés`, color: 'text-purple-500' },
            ].map((item, i) => (
              <motion.div
                key={item.text}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-card border border-border shadow-sm"
                whileHover={{ scale: 1.05, y: -2 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <motion.div animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 3, repeat: Infinity, delay: i * 0.5 }}>
                  <item.icon className={`h-4 w-4 ${item.color}`} />
                </motion.div>
                <span className="text-sm font-medium">{item.text}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div className="flex flex-wrap gap-2 justify-center mb-4" layout>
            {CATS.map(cat => (
              <motion.div key={cat.id} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant={filter === cat.id ? 'default' : 'outline'} size="sm" onClick={() => setFilter(cat.id)}>
                  <cat.icon className="h-4 w-4 mr-1" />
                  {cat.label}
                  {cat.id !== 'all' && counts[cat.id] ? ` (${counts[cat.id]})` : ''}
                </Button>
              </motion.div>
            ))}
          </motion.div>
          <div className="flex justify-center gap-2">
            {TYPE_FILTERS.map((t) => (
              <motion.div key={t.id} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant={typeFilter === t.id ? 'default' : 'outline'} size="sm" onClick={() => setTypeFilter(t.id)}>
                  {t.label}
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Content grid */}
      <section className="py-8 sm:py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {renderContent()}
        </div>
      </section>

      {/* Modal / Player */}
      <AnimatePresence>
        {selectedItem && selectedItem.type === 'tuto' && (
          <TutoPlayer item={selectedItem} onClose={() => setSelectedItem(null)} />
        )}
        {selectedItem && selectedItem.type !== 'tuto' && (
          <ConseilModal item={selectedItem} onClose={() => setSelectedItem(null)} />
        )}
      </AnimatePresence>

      {/* CTA */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--primary))] text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={VIEWPORT_ONCE}>
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Besoin d'un coup de main ?</h2>
            <p className="text-primary-foreground/80 mb-8">Nos Easers sont là pour vous faciliter la vie. Ménage, jardinage, bricolage : demandez un devis gratuit.</p>
            <Link to="/devis">
              <Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white shadow-lg">
                Demander un devis gratuit <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
